import { useState, useContext, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, IconButton, Typography, Grid, Tooltip } from '@material-ui/core';
import { Dialog, DialogContent, DialogTitle, CardMedia } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/CancelSharp';
import { makeStyles } from '@material-ui/core/styles';
import mixpanel from 'mixpanel-browser';

import GuideIcon from '@material-ui/icons/MenuBookSharp';
import { StitchIcon } from './Icons';

import { useThumbnailUrls, useMaterialsApi } from '../utils/materialsLib';
import { useMetadataApi } from '../utils/metadataLib';
import { onError } from '../utils/errorLib';
import { commonPrefix } from '../utils/parsingLib';
import TextFieldEditable from '../components/TextFieldEditable';
import { AppContext } from './AppState';
import ProgressButton from './ProgressButton';
import { stitchingGuideUrl } from '../utils/linksLib';


const stitchHelpText = 'Stitch the selected materials';
const gridSizeText = 'Stitch processing will automatically arrange the inputs';

const useStyles = makeStyles((theme) => ({
  modal: {
    minHeight: '300px',
    minWidth: '730px',
  },
  gradient: {
    display: 'flex',
    justifyContent: 'flex-end', // Align items to the right
    alignItems: 'center',
    height: '40px',
    color: theme.palette.secondary.light,
    paddingRight: theme.spacing(2),
    background: theme.palette.secondary.main,
  },
  guideButton: {
    marginTop: theme.spacing(-1),
    marginRight: theme.spacing(1), // Add spacing between guide and close button
  },
  guideIcon: {
    marginRight: theme.spacing(0.5),
  },
  closeButton: {
    marginTop: theme.spacing(-1),
  },
  iconButton: {
    marginLeft: theme.spacing(1),
  },
  stitchButton: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
  thumbnailGrid: {
    marginBottom: theme.spacing(3),
  },
  image: {
    height: 65,
    width: 105,
  },
  alignCenter: {
    display: 'flex',
    alignItems: 'center',
  },
  helpIconInline: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  spaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  invalidText: {
    color: 'red',
    float: 'right',
    marginRight: theme.spacing(0.3),
  },
  nameText: {
    minWidth: '35em',
  },
  nudgeDown: {
    marginTop: theme.spacing(2),
  },
}));


export default function StitchModal(props) {
  const { materials, visible, onHide } = props;

  const { getMaterial, stitchMaterial, processStitch } = useMaterialsApi();
  const { diffBrdfConfig } = useMetadataApi();

  const classes = useStyles();
  const [doingStitch, setDoingStitch] = useState(false);
  const [name, setName] = useState(null);
  const [successful, setSuccessful] = useState(false);
  const [inputsValidated, setInputsValidated] = useState(undefined);
  const [invalidText, setInvalidText] = useState();

  const { showAlert, } = useContext(AppContext);

  const { user } = useAuth0();

  useEffect(() => {
    async function validateInputs() {
      setInvalidText('');
      setInputsValidated(false);
      const keys = ['chartSize', 'texSize'];
      const { success, data } = await diffBrdfConfig(materials, keys);
      if (!success) {
        setInvalidText('Error validating stitch inputs');
      } else if (data.diffs.length > 0) {
        console.log('Mismatched stitched inputs:', data.diffs)
        setInvalidText('The sizes of your inputs do not match so they cannot be stitched');
      } else {
        setInputsValidated(true);
      }
    }
    if (visible && materials && user) {
      validateInputs();
    }
  }, [visible, materials, user]);

  useEffect(() => {
    if (visible && materials) {
      setName(defaultName(materials));
    }
  }, [visible, materials]);

  const { thumbnailUrls } = useThumbnailUrls(materials?.[0]?.userId, materials, 'StitchModal');
  const thumbnails = Object.values(thumbnailUrls);
  const imageCode = (visible && thumbnails && thumbnails.length > 0) ? (
    <Grid container spacing={1} className={classes.thumbnailGrid}>
      {thumbnails.map((t, i) =>
        <Grid item key={i}>
          <Tooltip title={materials[i].name}>
            <CardMedia
              className={classes.image}
              image={t}
            />
          </Tooltip>
        </Grid>
      )}
    </Grid>
  ) : '';

  function defaultName(mats) {
    var in_names = [];
    mats.forEach(mat => in_names.push(mat.name));
    const prefix = commonPrefix(in_names);
    var name = (prefix.length >= 8) ? prefix : in_names[0];
    name += '_STITCHED';
    return name;
  }

  async function doStitch() {
    setDoingStitch(true);
    var res = false;
    try {
      // Define the stitched material
      const materialIds = materials.map(mat => mat.materialId);
      mixpanel.track('User Stitch Start', { 'Inputs': materialIds, 'Material Name': name, 'Count': materials.length });
      const midOut = await stitchMaterial(materials[0].userId, materialIds, name);
      if (midOut) {
        // Process the stitched material
        const mat = await getMaterial(materials[0].userId, midOut);
        res = await processStitch(mat);
      }
    } catch (e) {
      onError(e);
    }

    if (res) {
      showAlert({ status: 'success', message: `Stitching processing started for new material ${name}` });
      setSuccessful(true);
      onHide();
    } else {
      showAlert({ status: 'error', message: 'An error occurred while processing the stitched material' });
    }
    setDoingStitch(false);
    return res;
  }

  const stitchButton =
    <Grid container className={classes.alignCenter}>
      <Grid item>
        <ProgressButton
          className={classes.stitchButton}
          disabled={!inputsValidated || !name || name?.length == 0 || doingStitch}
          loading={doingStitch}
          color='primary'
          variant='contained'
          onClick={doStitch}
          type='submit'
          success={successful}
          tooltip={'Create a stitched material from these inputs'}
        >
          Stitch
        </ProgressButton>
      </Grid>
      {/* <Grid item>
        <Tooltip title={stitchHelpText}>
          <HelpOutlineIcon className={classes.helpIconInline} color='secondary' />
        </Tooltip>
      </Grid> */}
    </Grid>;


  const stitchControls = (
    <Grid container className={classes.spaceBetween}>
      <Grid item xs={10}>
        <Grid container spacing={1} className={classes.alignCenter}>
          <Grid item xs={11}>
            <TextFieldEditable
              className={classes.nameText}
              label='Stitched material name'
              value={name}
              setValue={setName}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Grid container spacing={1}  className={classes.alignCenter}>
          <Grid item>
            {stitchButton}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  return (<>
    <Dialog classes={{paper: classes.modal}} centered='true' open={visible} onClose={onHide}>
      <div className={classes.gradient}>
      </div>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
          <Grid item>
            <Typography variant="h6">
              <StitchIcon /> Stitch {materials.length} input materials
            </Typography>
          </Grid>
          <Grid item>
            <Grid container justifyContent="flex-end" alignItems="center" spacing={1}>
              <Grid item>
                <Button
                  className={classes.guideButton}
                  color='primary'
                  onClick={() => window.open(stitchingGuideUrl, '_blank')}
                >
                  <GuideIcon className={classes.guideIcon} />
                  Stitching guide
                </Button>
              </Grid>
              <Grid item>
                <IconButton onClick={onHide} className={classes.closeButton}>
                  <CancelIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Grid container>
          <Grid item xs={12}>
            <Typography>
              Inputs
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {imageCode}
          </Grid>
          <Grid item xs={12} className={classes.invalidText}>
            <Typography variant='caption' className={classes.invalidText}>
              {invalidText}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {stitchControls}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  </>);
}
